import React, { useState } from 'react';
import { Button, Modal, Form, Input, InputNumber, Table, Space, Tag, Switch, Card, Typography, Row, Col, Select } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import apiService from '../../../services/api';
import { mentorProfileQueryKeys } from '../../../lib/queryClient';
import { MentorServiceResponse, CreateMentorServiceRequest, UpdateMentorServiceRequest } from '../../../types';
import PanelTabCard from '../../../components/Common/PanelTabCard';
import TabListLoading from '../../../components/Common/TabListLoading';

const { Title } = Typography;
const { Option } = Select;

const SERVICE_TYPES = [
  { value: 'OneOnOne', label: 'Birebir Mentoring', color: 'blue' },
  { value: 'Group', label: 'Grup Eğitimi', color: 'green' },
  { value: 'Workshop', label: 'Workshop', color: 'purple' }
];

const ServicesTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [modalVisible, setModalVisible] = useState(false);
  const [editingService, setEditingService] = useState<MentorServiceResponse | null>(null);
  const [form] = Form.useForm();

  const { data: services = [], isLoading: loading } = useQuery({
    queryKey: mentorProfileQueryKeys.services,
    queryFn: async () => {
      const res = await apiService.getMentorServices();
      if (!res.success) throw new Error(res.message || 'Hizmetler yüklenemedi.');
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: CreateMentorServiceRequest) => apiService.createMentorService(data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Hizmet başarıyla eklendi.');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.services });
        setModalVisible(false);
      } else toast.error(res.message || 'Hizmet eklenemedi.');
    },
    onError: () => toast.error('Hizmetler yüklenirken hata oluştu.'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdateMentorServiceRequest }) =>
      apiService.updateMentorService(id, data),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Hizmet başarıyla güncellendi.');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.services });
        setModalVisible(false);
        setEditingService(null);
        form.resetFields();
      } else toast.error(res.message || 'Hizmet güncellenemedi.');
    },
    onError: () => toast.error('Hizmet güncellenirken hata oluştu.'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiService.deleteMentorService(id),
    onSuccess: (res) => {
      if (res.success) {
        toast.success('Hizmet başarıyla silindi.');
        queryClient.invalidateQueries({ queryKey: mentorProfileQueryKeys.services });
      } else toast.error(res.message || 'Hizmet silinemedi.');
    },
    onError: () => toast.error('Hizmet silinirken hata oluştu.'),
  });

  const handleAdd = () => {
    setEditingService(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (service: MentorServiceResponse) => {
    setEditingService(service);
    form.setFieldsValue({
      serviceName: service.serviceName,
      description: service.description,
      price: service.price,
      durationInMinutes: service.durationInMinutes,
      serviceType: service.serviceType,
      requirements: service.requirements,
      isActive: service.isActive
    });
    setModalVisible(true);
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      const serviceData = {
        serviceName: values.serviceName,
        description: values.description,
        price: values.price,
        durationInMinutes: values.durationInMinutes,
        serviceType: values.serviceType,
        requirements: values.requirements,
        isActive: values.isActive
      };
      if (editingService) {
        updateMutation.mutate({ id: editingService.id, data: serviceData });
      } else {
        createMutation.mutate(serviceData);
      }
    } catch {
      // form validation
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setEditingService(null);
    form.resetFields();
  };

  const handleDelete = (id: number) => deleteMutation.mutate(id);

  // Filtreleme
  const filteredServices = services.filter(service =>
    service.serviceName.toLowerCase().includes(searchText.toLowerCase()) ||
    service.serviceType.toLowerCase().includes(searchText.toLowerCase()) ||
    (service.description && service.description.toLowerCase().includes(searchText.toLowerCase()))
  );

  const columns = [
    {
      title: 'Hizmet Adı',
      dataIndex: 'serviceName',
      key: 'serviceName',
      sorter: (a: MentorServiceResponse, b: MentorServiceResponse) => a.serviceName.localeCompare(b.serviceName),
    },
    {
      title: 'Tür',
      dataIndex: 'serviceType',
      key: 'serviceType',
      render: (type: string) => {
        const typeConfig = SERVICE_TYPES.find(t => t.value === type);
        return <Tag color={typeConfig?.color || 'default'}>{typeConfig?.label || type}</Tag>;
      },
      filters: SERVICE_TYPES.map(type => ({ text: type.label, value: type.value })),
      onFilter: (value: boolean | React.Key, record: MentorServiceResponse) => record.serviceType === value,
    },
    {
      title: 'Süre',
      dataIndex: 'durationInMinutes',
      key: 'durationInMinutes',
      render: (minutes: number) => `${minutes} dk`,
      sorter: (a: MentorServiceResponse, b: MentorServiceResponse) => a.durationInMinutes - b.durationInMinutes,
    },
    {
      title: 'Ücret (₺)',
      dataIndex: 'price',
      key: 'price',
      render: (price: number) => `${price}₺`,
      sorter: (a: MentorServiceResponse, b: MentorServiceResponse) => a.price - b.price,
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      ),
      filters: [
        { text: 'Aktif', value: true },
        { text: 'Pasif', value: false }
      ],
      onFilter: (value: boolean | React.Key, record: MentorServiceResponse) => record.isActive === value,
    },
    {
      title: 'Oluşturulma Tarihi',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY'),
      sorter: (a: MentorServiceResponse, b: MentorServiceResponse) => 
        new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: MentorServiceResponse) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: 'var(--text-secondary)' }}
          >
            Düzenle
          </Button>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record.id)}
          >
            Sil
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <PanelTabCard
      title="Hizmetlerim"
      description="Sunmak istediğiniz mentoring hizmetlerini ekleyin veya düzenleyin."
    >
      <TabListLoading loading={loading}>
      <Card style={{ marginBottom: '16px' }}>
        <Row justify="space-between" align="middle" style={{ marginBottom: '16px' }}>
          <Col>
          </Col>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
              style={{
                backgroundColor: 'var(--text-secondary)',
                borderColor: 'var(--text-secondary)',
                fontWeight: 500
              }}
            >
              Yeni Hizmet
            </Button>
          </Col>
        </Row>
        
        <Row style={{ marginBottom: '16px' }}>
          <Col span={8}>
            <Input
              placeholder="Hizmet adı, tür veya açıklama ara..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ borderRadius: '6px' }}
            />
          </Col>
        </Row>

        <Table
          scroll={{ x: 'max-content' }}
          columns={columns}
          dataSource={filteredServices}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} hizmet`
          }}
        />
      </Card>
      </TabListLoading>

      <Modal
        title={editingService ? 'Hizmet Düzenle' : 'Yeni Hizmet'}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        okText={editingService ? 'Güncelle' : 'Ekle'}
        cancelText="İptal"
        okButtonProps={{
          style: { backgroundColor: 'var(--text-secondary)', borderColor: 'var(--text-secondary)' }
        }}
      >
        <Form layout="vertical" form={form} requiredMark={false}>
          <Form.Item 
            name="serviceName" 
            label="Hizmet Adı" 
            rules={[{ required: true, message: 'Hizmet adı gerekli' }]}
          >
            <Input placeholder="Örn: C# Eğitimi, React Mentoring" />
          </Form.Item>

          <Form.Item 
            name="description" 
            label="Açıklama"
          >
            <Input.TextArea 
              rows={4} 
              placeholder="Hizmet hakkında detaylı açıklama..."
            />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item 
                name="price" 
                label="Saatlik Ücret (₺)" 
                rules={[{ required: true, message: 'Ücret gerekli' }]}
              >
                <InputNumber 
                  min={0} 
                  step={50} 
                  style={{ width: '100%' }} 
                  placeholder="0"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item 
                name="durationInMinutes" 
                label="Süre (Dakika)" 
                rules={[{ required: true, message: 'Süre gerekli' }]}
                initialValue={60}
              >
                <InputNumber 
                  min={15} 
                  max={480} 
                  step={15} 
                  style={{ width: '100%' }} 
                  placeholder="60"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item 
            name="serviceType" 
            label="Hizmet Türü" 
            rules={[{ required: true, message: 'Hizmet türü seçin' }]}
            initialValue="OneOnOne"
          >
            <Select placeholder="Hizmet türü seçin">
              {SERVICE_TYPES.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item 
            name="requirements" 
            label="Gereksinimler"
          >
            <Input.TextArea 
              rows={3} 
              placeholder="Katılımcıların sahip olması gereken ön bilgiler..."
            />
          </Form.Item>

          <Form.Item name="isActive" label="Aktif" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </PanelTabCard>
  );
};

export default ServicesTab;
